// src/components/SubjectsPage.tsx
import React, { useState, useMemo, useEffect } from 'react';
import AssetManager from '../services/AssetManager';
import Card from './ui/Card';
import Button from './ui/Button';
import Modal from './ui/Modal';
import Input from './ui/Input';
import MultiSelectDropdown from './ui/MultiSelectDropdown';
import Subject from '../models/Subject';
import Room from '../models/Room';

// Type for the data we keep in the "Add / Edit" modal. We don’t use the full Subject model
// because while creating a subject it has no ID yet and while editing we only need a
// subset of fields plus the temporary `allowedRooms` array used by the UI.
interface SubjectFormData {
    id: string | null;
    subjectCode?: string;
    name: string;
    color: string;
    allowedRooms: string[];
}

// Reusable Icons
const TrashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);
const CheckIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white mix-blend-difference" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
);

const colorPalette = [
    '#fecaca', '#fca5a5', '#fecdd3', '#f9a8d4',
    '#fed7aa', '#fdba74', '#fef08a', '#fde047',
    '#d9f99d', '#a7f3d0', '#a5f3fc', '#6ee7b7',
    '#bae6fd', '#c7d2fe', '#e9d5ff', '#ddd6fe',
];

const DEFAULT_COLOR = colorPalette[0] as string;

const assetManager = AssetManager.getInstance();

function SubjectsPage() {
    const [subjects, setSubjects] = useState<Subject[]>([]);
    const [rooms, setRooms] = useState<Room[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [filterText, setFilterText] = useState<string>('');
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
    const [currentSubject, setCurrentSubject] = useState<SubjectFormData | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const [fetchedSubjects, fetchedRooms] = await Promise.all([
                    assetManager.fetchSubjects(),
                    assetManager.fetchRooms()
                ]);
                setSubjects(fetchedSubjects);
                setRooms(fetchedRooms);
            } catch (err: any) {
                setError(err.message || 'Failed to fetch data.');
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const roomNames = useMemo(() => rooms.map(r => r.name), [rooms]);

    const filteredSubjects = useMemo(() => {
        if (!filterText) return subjects;
        return subjects.filter(subject =>
            subject.name.toLowerCase().includes(filterText.toLowerCase())
        );
    }, [subjects, filterText]);

    const openModal = (mode: 'add' | 'edit', subject?: Subject) => {
        setModalMode(mode);
        if (mode === 'add') {
            setCurrentSubject({ id: null, name: '', color: DEFAULT_COLOR, allowedRooms: [] });
        } else if (subject) {
            const subjectColor = subject.color || DEFAULT_COLOR;
            const associatedRooms = subject.allowedRooms || [];
            setCurrentSubject({ ...subject, color: subjectColor, allowedRooms: associatedRooms });
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setCurrentSubject(null);
        setModalMode('add');
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setCurrentSubject(prev => prev ? { ...prev, [name]: value } : null);
    };

    const handleColorSelect = (color: string) => {
        setCurrentSubject(prev => prev ? { ...prev, color } : null);
    };

    const handleRoomSelectionChange = (selectedRooms: string[]) => {
        setCurrentSubject(prev => prev ? { ...prev, allowedRooms: selectedRooms } : null);
    };

    const handleSaveSubject = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!currentSubject || !currentSubject.name.trim()) {
            alert('Please enter a subject name.');
            return;
        }
        const subjectData = {
            ...currentSubject,
            name: currentSubject.name.trim(),
            color: currentSubject.color || colorPalette[0],
            associatedRooms: Array.isArray(currentSubject.allowedRooms) ? currentSubject.allowedRooms : [],
        } as const;

        try {
            if (modalMode === 'add') {
                const newSubject = await assetManager.createSubject(subjectData);
                setSubjects(prev => [...prev, newSubject]);
            } else {
                const updatedSubject = await assetManager.updateSubject(subjectData.id as string, subjectData);
                setSubjects(prev => prev.map(s => s.id === updatedSubject.id ? updatedSubject : s));
            }
            closeModal();
        } catch (err: any) {
            alert(`Error saving subject: ${err.message}`);
            console.error(err);
        }
    };

    const handleDeleteSubject = async () => {
        if (!currentSubject || !currentSubject.id) return;
        const subjectIdToDelete = currentSubject.id;
        if (window.confirm(`Are you sure you want to delete subject "${currentSubject.name}"? This action cannot be undone.`)) {
            try {
                await assetManager.deleteSubject(subjectIdToDelete);
                setSubjects(prev => prev.filter(s => s.id !== subjectIdToDelete));
                closeModal();
            } catch (err: any) {
                alert(`Error deleting subject: ${err.message}`);
                console.error(err);
            }
        }
    };

    if (isLoading) {
        return <div className="p-8 text-center">Loading subjects...</div>;
    }

    if (error) {
        return <div className="p-8 text-center text-red-500">Error: {error}</div>;
    }

    return (
        <div className="p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen">
            <Card className="mb-6 p-4" onClick={() => { }}>
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div className="w-full sm:w-auto sm:flex-1">
                        <Input
                            type="text"
                            placeholder="Filter subjects by name..."
                            value={filterText}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterText(e.target.value)}
                            className="w-full"
                            label=""
                            id="filter-subjects"
                            name="filter"
                            required={false}
                            autoFocus={false}
                            minLength={0}
                        />
                    </div>
                    <Button
                        variant="primary"
                        className="text-sm py-2 px-4 flex items-center space-x-1 w-full sm:w-auto"
                        onClick={() => openModal('add')}
                        title="Add new subject"
                        aria-label="Add new subject"
                    >
                        <span className="text-lg font-bold leading-none mr-1">+</span>
                        <span>Add Subject</span>
                    </Button>
                </div>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {filteredSubjects.length > 0 ? (
                    filteredSubjects.map((subject) => (
                        <Card
                            key={subject.id}
                            className="shadow hover:shadow-lg transition-shadow duration-150 cursor-pointer active:shadow-inner"
                            onClick={() => openModal('edit', subject)}
                        >
                            <div className="p-2">
                                <div
                                    className="w-full h-3 rounded-t mb-1 border border-gray-200"
                                    style={{ backgroundColor: subject.color || colorPalette[0] }}
                                    title={`Color: ${subject.color || colorPalette[0]}`}
                                ></div>

                                <h3 className="text-sm font-semibold text-gray-800 truncate mb-1" title={subject.name}>
                                    {subject.name}
                                </h3>

                                <div className="text-[10px] text-gray-500 mb-1 min-h-0 leading-tight" title={subject.allowedRooms?.join(', ')}>
                                    {subject.allowedRooms && subject.allowedRooms.length > 0 ? (
                                        <>
                                            <span className="font-medium">Rooms:</span> {subject.allowedRooms.join(', ') || '-'}
                                        </>
                                    ) : (
                                        <span className="italic">All rooms</span>
                                    )}
                                </div>
                            </div>
                        </Card>
                    ))
                ) : (
                    <p className="text-gray-500 col-span-full text-center py-4">
                        {filterText ? 'No subjects match your filter.' : 'No subjects defined yet.'}
                    </p>
                )}
            </div>

            <Modal isOpen={isModalOpen} onClose={closeModal} title={modalMode === 'add' ? 'Add New Subject' : 'Edit Subject'}>
                {currentSubject && (
                    <form onSubmit={handleSaveSubject} className="space-y-4">
                        <div>
                            <label htmlFor="subject-name" className="block text-sm font-medium text-gray-700 mb-1">Subject Name</label>
                            <Input
                                id="subject-name"
                                name="name"
                                value={currentSubject.name}
                                onChange={handleInputChange}
                                placeholder="Enter subject name"
                                required
                                autoFocus={modalMode === 'add'}
                                label=""
                                minLength={0}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                            <div className="flex flex-wrap gap-1">
                                {colorPalette.map(color => (
                                    <button
                                        key={color}
                                        type="button"
                                        className={`w-6 h-6 rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-blue-500 flex items-center justify-center ${currentSubject.color === color ? 'ring-2 ring-blue-600 ring-offset-1' : ''}`}
                                        style={{ backgroundColor: color }}
                                        onClick={() => handleColorSelect(color)}
                                        title={color}
                                    >
                                        {currentSubject.color === color && <CheckIcon />}
                                    </button>
                                ))}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Selected: {currentSubject.color}</p>
                        </div>

                        <div>
                            <label htmlFor="subject-rooms" className="block text-sm font-medium text-gray-700 mb-1">
                                Associated Rooms (Optional)
                                <span className="text-xs text-gray-500 ml-1">- Only allow booking in selected rooms</span>
                            </label>
                            <MultiSelectDropdown
                                options={roomNames}
                                selectedOptions={currentSubject.allowedRooms || []}
                                onChange={handleRoomSelectionChange}
                                placeholder="Available in all rooms"
                            />
                        </div>

                        <div className="mt-6 flex justify-between items-center pt-4 border-t">
                            <div>
                                {modalMode === 'edit' && (
                                    <Button
                                        type="button"
                                        variant="danger"
                                        onClick={handleDeleteSubject}
                                        className="flex items-center space-x-1"
                                        title="Delete this subject"
                                        aria-label="Delete this subject"
                                    >
                                        <TrashIcon />
                                        <span>Delete Subject</span>
                                    </Button>
                                )}
                            </div>
                            <div className="flex space-x-3">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={closeModal}
                                    title="Cancel editing"
                                    aria-label="Cancel editing"
                                >Cancel</Button>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    onClick={() => { }}
                                    title={modalMode === 'add' ? 'Add subject' : 'Save changes'}
                                    aria-label={modalMode === 'add' ? 'Add subject' : 'Save changes'}
                                >{modalMode === 'add' ? 'Add Subject' : 'Save Changes'}</Button>
                            </div>
                        </div>
                    </form>
                )}
            </Modal>
        </div>
    );
}

export default SubjectsPage; 